import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import PublicRoute from "./components/layout/PublicRoute";
import Layout from "./components/layout/Layout";
import LoadingOverlay from "./components/ui/LoadingOverlay";
import "./index.css";

const Login = lazy(() => import("./pages/auth/LoginPage"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPasswordPage"));
const Register = lazy(() => import("./pages/auth/RegisterPage"));
const TwoFactorVerify = lazy(() => import("./pages/auth/TwoFactorVerifyPage"));
const SetupTwoFactor = lazy(() => import("./pages/auth/SetupTwoFactorPage"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmailPage"));
const Dashboard = lazy(() => import("./pages/DashboardPage"));
const Admin = lazy(() => import("./pages/AdminUsersPage"));
const UserDetail = lazy(() => import("./pages/UserDetailPage"));
const BidLists = lazy(() => import("./pages/BidListPage"));
const MarketCondition = lazy(() => import("./pages/MarketConditionPage"));
const Parade = lazy(() => import("./pages/ParadePage"));
const RXO = lazy(() => import("./pages/RxoPage"));
const LoadDetail = lazy(() => import("./pages/LoadDetailPage"));
const Settings = lazy(() => import("./pages/SettingsPage"));
const Health = lazy(() => import("./pages/HealthPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPage"));
const TermsAndConditions = lazy(() => import("./pages/TermsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const DemoBoard = lazy(() => import("./pages/demo/DemoBoardPage"));

const RouteFallback = () => <LoadingOverlay />;

/** Suspense for routes outside the authenticated shell (no navbar yet). */
const LazyPage = ({ children }) => (
  <Suspense fallback={<RouteFallback />}>{children}</Suspense>
);

function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <LazyPage>
            <PublicRoute>
              <Login />
            </PublicRoute>
          </LazyPage>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <LazyPage>
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          </LazyPage>
        }
      />
      <Route
        path="/reset-password"
        element={
          <LazyPage>
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          </LazyPage>
        }
      />
      <Route
        path="/register"
        element={
          <LazyPage>
            <PublicRoute>
              <Register />
            </PublicRoute>
          </LazyPage>
        }
      />
      <Route
        path="/verify-email"
        element={
          <LazyPage>
            <PublicRoute>
              <VerifyEmail />
            </PublicRoute>
          </LazyPage>
        }
      />
      <Route
        path="/verify-2fa"
        element={
          <LazyPage>
            <PublicRoute>
              <TwoFactorVerify />
            </PublicRoute>
          </LazyPage>
        }
      />
      <Route
        path="/setup-2fa"
        element={
          <LazyPage>
            <ProtectedRoute allowWithout2FA>
              <SetupTwoFactor />
            </ProtectedRoute>
          </LazyPage>
        }
      />

      {/* Public legal pages (no auth required) */}
      <Route
        path="/privacy"
        element={
          <LazyPage>
            <PrivacyPolicy />
          </LazyPage>
        }
      />
      <Route
        path="/terms"
        element={
          <LazyPage>
            <TermsAndConditions />
          </LazyPage>
        }
      />
      {/* Layout stays mounted; page Suspense lives inside Layout around <Outlet /> */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/admin/users" element={<Admin />} />
        <Route path="/admin/users/:id" element={<UserDetail />} />
        <Route path="/admin/health" element={<Health />} />
        <Route path="/bids" element={<BidLists />} />
        <Route path="/market-condition" element={<MarketCondition />} />
        <Route path="/parade" element={<Parade />} />
        <Route path="/rxo" element={<RXO />} />
        <Route path="/load/:loadId" element={<LoadDetail />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/demo" element={<DemoBoard />} />
      </Route>

      <Route
        path="*"
        element={
          <LazyPage>
            <NotFoundPage />
          </LazyPage>
        }
      />
    </Routes>
  );
}

export default App;
